import { Injectable, computed, signal } from "@angular/core";

export interface BusinessSalonOption {
  salon_id: string;
  name: string;
  city?: string | null;
}

@Injectable({
  providedIn: "root",
})
export class BusinessSalonContextService {
  private readonly storageKey = "gloget_business_selected_salons";

  private readonly _salons = signal<BusinessSalonOption[]>([]);

  private readonly _selectedSalonIds = signal<string[]>([]);

  readonly salons = this._salons.asReadonly();

  readonly selectedSalonIds = this._selectedSalonIds.asReadonly();

  /*
   * Every salon is selected.
   */
  readonly allSelected = computed(() => {
    const salons = this._salons();

    const selected = this._selectedSalonIds();

    return salons.length > 0 && selected.length === salons.length;
  });

  /*
   * Nothing is selected.
   */
  readonly noneSelected = computed(() => {
    return this._selectedSalonIds().length === 0;
  });

  readonly selectionLabel = computed(() => {
    const salons = this._salons();

    const selected = this._selectedSalonIds();

    if (!salons.length) {
      return "No salons";
    }

    if (!selected.length) {
      return "No salons selected";
    }

    if (selected.length === salons.length) {
      return "All salons";
    }

    if (selected.length === 1) {
      return (
        salons.find((salon) => salon.salon_id === selected[0])?.name ??
        "1 salon"
      );
    }

    return `${selected.length} salons`;
  });

  /*
   * ======================================================
   * Initialize / refresh salon list
   * ======================================================
   */

  setSalons(salons: BusinessSalonOption[]): void {
    const normalized = (salons ?? []).map((salon) => ({
      ...salon,

      salon_id: String(salon.salon_id),
    }));

    this._salons.set(normalized);

    const availableIds = new Set(normalized.map((salon) => salon.salon_id));

    /*
     * Important:
     *
     * null = no saved preference yet
     * []   = user intentionally selected none
     */
    const stored = this.readStoredSelection();

    if (stored !== null) {
      const validStored = stored.filter((id) => availableIds.has(id));

      this._selectedSalonIds.set(validStored);

      this.persistSelection(validStored);

      return;
    }

    /*
     * First visit defaults to all salons.
     */
    this.selectAll();
  }

  /*
   * ======================================================
   * Selection
   * ======================================================
   */

  selectAll(): void {
    this.setSelected(this._salons().map((salon) => salon.salon_id));
  }

  clearAll(): void {
    this.setSelected([]);
  }

  /*
   * Used by the "All salons" checkbox.
   *
   * If everything is already selected:
   *   clear everything.
   *
   * Otherwise:
   *   select everything.
   */
  toggleAll(): void {
    if (this.allSelected()) {
      this.clearAll();
      return;
    }

    this.selectAll();
  }

  toggleSalon(salonId: string): void {
    salonId = String(salonId);

    const current = [...this._selectedSalonIds()];

    if (current.includes(salonId)) {
      this.setSelected(current.filter((id) => id !== salonId));

      return;
    }

    this.setSelected([...current, salonId]);
  }

  isSelected(salonId: string): boolean {
    return this._selectedSalonIds().includes(String(salonId));
  }

  setSelected(salonIds: string[]): void {
    const availableIds = new Set(this._salons().map((salon) => salon.salon_id));

    const selected = [
      ...new Set(
        (salonIds ?? []).map(String).filter((id) => availableIds.has(id)),
      ),
    ];

    /*
     * Empty array is now completely valid.
     */
    this._selectedSalonIds.set(selected);

    this.persistSelection(selected);
  }

  /*
   * ======================================================
   * API scope
   * ======================================================
   */

  /*
   * null
   *     = every salon
   *
   * "none"
   *     = zero salons
   *
   * "uuid1,uuid2"
   *     = selected salons
   */
  getFilterValue(): string | null {
    const salons = this._salons();

    const selected = this._selectedSalonIds();

    if (salons.length > 0 && selected.length === salons.length) {
      return null;
    }

    if (!selected.length) {
      return "none";
    }

    return selected.join(",");
  }

  withSalonScope(url: string): string {
    const filter = this.getFilterValue();

    /*
     * All salons = preserve existing
     * unfiltered backend behavior.
     */
    if (filter === null) {
      return url;
    }

    const separator = url.includes("?") ? "&" : "?";

    return url + separator + "salon_ids=" + encodeURIComponent(filter);
  }

  /*
   * ======================================================
   * Storage
   * ======================================================
   */

  private persistSelection(selected: string[]): void {
    if (typeof localStorage === "undefined") {
      return;
    }

    localStorage.setItem(this.storageKey, JSON.stringify(selected));
  }

  private readStoredSelection(): string[] | null {
    if (typeof localStorage === "undefined") {
      return null;
    }

    const raw = localStorage.getItem(this.storageKey);

    /*
     * No stored preference.
     */
    if (raw === null) {
      return null;
    }

    try {
      const value = JSON.parse(raw);

      return Array.isArray(value) ? value.map(String) : null;
    } catch {
      return null;
    }
  }
}
